<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <!-- Value Input Module -->
  <InputPopupModule
    v-model:show="isOpen"
    :title="title"
    :subtitle="subtitle"
    :input-label="inputLabel"
    :save-label="saveLabel"
    :cancel-label="cancelLabel"
    :initial-data="initialFormData"
    :validation-fn="validateForm"
    @save="handleSave"
    @cancel="handleCancel">
    <template #content="{ formData, updateField, submitForm }">
      <div v-if="showDateInput" class="mb-4">
        <label class="block text-sm font-medium text-[var(--color-text)] mb-2">
          {{ $t('views.session.famacha.dateLabel') }}
        </label>
        <div class="flex items-center gap-2">
          <input
            :value="formData.date"
            @input="updateField('date', $event.target.value)"
            type="date"
            :max="getLocalDateString()"
            class="input-field flex-1 w-full"
            :class="{ 'flash-animation': isFlashing }" />
          <button
            @click="
              () => {
                updateField('date', getLocalDateString())
                triggerFlash()
              }
            "
            class="btn btn--secondary timer-reset-btn"
            :class="{ 'btn--active': isFlashing }"
            :title="$t('common.buttons.now')">
            <CalendarSync />
          </button>
        </div>
        <!-- Date validation error -->
        <div
          v-if="getDateValidationError(formData.date)"
          class="text-xs text-red-500 dark:text-red-400 mt-1">
          {{ getDateValidationError(formData.date) }}
        </div>
      </div>

      <label class="block text-sm font-medium text-[var(--color-text)] mb-2">
        {{ inputLabel }}
      </label>

      

      <!-- FAMACHA Score Input -->
      <div v-if="props.inputMode === 'famacha'" class="mb-2">
        <div class="flex gap-2 justify-center">
          <div
            v-for="score in [1, 2, 3, 4, 5]"
            :key="score"
            class="flex flex-col items-center">
            <button
              @click="updateField('score', score)"
              type="button"
              class="famacha-score-btn"
              :class="{
                'famacha-score-btn--selected': formData.score === score,
                'famacha-score-1': score === 1,
                'famacha-score-2': score === 2,
                'famacha-score-3': score === 3,
                'famacha-score-4': score === 4,
                'famacha-score-5': score === 5,
              }">
              {{ score }}
            </button>
            <p
              class="text-[10px] text-center text-[var(--color-text)] mt-1 max-w-[3rem] leading-tight">
              {{ getScoreShortLabel(score) }}
            </p>
          </div>
        </div>
        <p class="text-xs text-center text-[var(--color-text)] mt-2">
          {{ getScoreDescription(formData.score) }}
        </p>
      </div>

      <!-- DAG Score Input -->
    <div v-else-if="props.inputMode === 'dag'" class="mb-2">
      <div class="flex gap-2 justify-center">
        <div
          v-for="score in [1, 2, 3, 4, 5]"
          :key="score"
          class="flex flex-col items-center">
          <button
          @click="updateField('score', score)"
          type="button"
          class="dag-score-btn"
          :class="{
            'dag-score-btn--selected': formData.score === score,
            'dag-score-1': score === 1,
            'dag-score-2': score === 2,
            'dag-score-3': score === 3,
            'dag-score-4': score === 4,
            'dag-score-5': score === 5,
          }">
          {{ score }}
        </button>
        <p class="text-[10px] text-center text-[var(--color-text)] mt-1 max-w-[3rem] leading-tight">
          {{ getDagScoreShortLabel(score) }}
        </p>
      </div>
    </div>

    <p class="text-xs text-center text-[var(--color-text)] mt-2">
     {{ getDagScoreDescription(formData.score) }}
    </p>
  </div>


      <!-- Wound/Treatment Input -->
      <div v-else-if="props.inputMode === 'wound' || props.inputMode === 'treatment'" class="space-y-4">
        <!-- Type Selection -->
        <div>
          <select
            :value="formData.healthType"
            @change="updateField('healthType', $event.target.value)"
            class="dropdown-select w-full p-3 pr-10 rounded-lg border border-[var(--color-primary-400)] bg-[var(--color-primary-100)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-highlight)]">
            <option 
              v-for="type in getHealthTypeOptions()"
              :key="type"
              :value="type">
              {{ getHealthTypeLabel(type) }}
            </option>
          </select>
        </div>

        <!-- Title -->
        <div>
          <label class="block text-sm font-medium text-[var(--color-text)] mb-2">
            {{ getHealthRecordLabel('title') }}
          </label>
          <input
            :value="formData.healthTitle"
            @input="updateField('healthTitle', $event.target.value)"
            type="text"
            :placeholder="getHealthRecordLabel('titlePlaceholder')"
            class="input-field w-full" />
        </div>

        <!-- Description -->
        <div>
          <label class="block text-sm font-medium text-[var(--color-text)] mb-2">
            {{ getHealthRecordLabel('description') }}
          </label>
          <textarea
            :value="formData.healthDescription"
            @input="updateField('healthDescription', $event.target.value)"
            :placeholder="getHealthRecordLabel('descriptionPlaceholder')"
            rows="3"
            class="input-field w-full resize-none"></textarea>
        </div>
      </div>

      <!-- Multi-line text input -->
      <textarea
        v-else-if="props.inputMode === 'textarea'"
        :value="formData.value"
        @input="handleInput($event, updateField)"
        @keydown.ctrl.enter.prevent="submitForm()"
        :rows="textareaRows"
        class="input-field textarea-field w-full"
        :placeholder="placeholder"
        ref="inputRef" />
      <!-- Decimal input -->
      <input
        v-else-if="props.inputMode === 'decimal'"
        :value="formData.value"
        @input="handleInput($event, updateField)"
        @keydown.enter.prevent="submitForm()"
        type="number"
        step="0.1"
        min="1"
        max="100"
        class="input-field input-field--center w-full"
        :placeholder="placeholder"
        ref="inputRef" />

      <!-- Single-line text input -->
      <input
        v-else
        :value="formData.value"
        @input="handleInput($event, updateField)"
        @keydown.enter.prevent="submitForm()"
        class="input-field input-field--center w-full"
        :placeholder="placeholder"
        ref="inputRef" />
    </template>
  </InputPopupModule>
</template>

<script setup>
  import { ref, computed, watch, nextTick } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { CalendarSync } from 'lucide-vue-next'
  import InputPopupModule from '@/components/ui/inputs/InputPopupModule.vue'
  import { useGoatStore } from '@/stores/goat.store.js'

  const { t } = useI18n()
  const goatStore = useGoatStore()

  const props = defineProps({
    show: { type: Boolean, default: false },
    title: { type: String, required: true },
    subtitle: { type: String, default: '' },
    placeholder: { type: String, default: '' },

    inputMode: {
      type: String,
      default: 'textarea',
      validator: (value) =>
        ['textarea', 'number', 'decimal', 'famacha', 'dag', 'wound', 'treatment'].includes(value),
    },

    textareaRows: { type: Number, default: 4 },

    validation: {
      type: Object,
      default: () => ({}),
    },

    inputLabel: { type: String, default: '' },
    saveLabel: { type: String, default: '' },
    cancelLabel: { type: String, default: '' },
    centerText: { type: Boolean, default: null },
    showDateInput: { type: Boolean, default: false },
    goatId: { type: Number, default: null },
    goatName: { type: String, default: '' },

    initialValue: { type: [String, Number], default: '' },
    initialData: { type: Object, default: null },
  })

  const emit = defineEmits(['save', 'cancel', 'update:show'])

  const inputRef = ref(null)
  const isFlashing = ref(false)

  const getLocalDateString = () => {
    return new Date().toISOString().split('T')[0]
  }

  // Initialize form data with today's date and initial value
  const initialFormData = computed(() => {
    if (props.inputMode === 'wound' || props.inputMode === 'treatment') {
      if (props.initialData) {
        return {
          healthType: props.initialData.type || (props.inputMode === 'wound' ? 'wound' : 'treatment'),
          healthTitle: props.initialData.title || '',
          healthDescription: props.initialData.description || '',
          date: props.initialData.date || getLocalDateString(),
        }
      }
      return {
        healthType: props.inputMode === 'wound' ? 'wound' : 'treatment',
        healthTitle: '',
        healthDescription: '',
        date: getLocalDateString(),
      }
    }
    return {
      value: props.initialValue?.toString() || '',
      date: getLocalDateString(),
      score: null,
    }
  })

  // Two-way binding for show prop
  const isOpen = computed({
    get: () => props.show,
    set: (value) => emit('update:show', value),
  })

  // Auto-focus when module opens
  watch(
    () => props.show,
    async (newValue) => {
      if (newValue) {
        await nextTick()
        await nextTick()
        inputRef.value?.focus()
      }
    }
  )

  // Get goat from store using goatId prop
  const goat = computed(() => {
    return props.goatId
      ? goatStore.goats.find((g) => g.id === props.goatId)
      : null
  })

  function handleInput(e, updateField) {
    let val = e.target.value

    // Handle text inputs - allow all input
    if (props.inputMode === 'textarea') {
      updateField('value', val)
      return
    }

    // For numeric inputs (number or decimal), only allow digits, comma, and dot
    if (props.inputMode === 'number' || props.inputMode === 'decimal') {
      val = val.replace(/[^0-9.,]/g, '')

      // Handle decimal places
      const parts = val.split(/[.,]/)
      if (parts.length > 2) val = parts[0] + '.' + parts[1]

      // Specific validation for single digit numbers
      if (props.inputMode === 'number' && props.validation?.type === 'range') {
        const { min, max } = props.validation
        if (min >= 1 && max <= 9) {
          val = val.replace(new RegExp(`[^${min}-${max}]`, 'g'), '')
          if (val.length > 1) val = val[0] // Only allow single digit
        }
      }
    }

    updateField('value', val)
  }
  function getDagScoreShortLabel(score) {
  // optional: wenn ihr keine Texte wollt, einfach return ''
  const map = {
    1: t('views.session.dag.score1'),
    2: t('views.session.dag.score2'),
    3: t('views.session.dag.score3'),
    4: t('views.session.dag.score4'),
    5: t('views.session.dag.score5'),
  }
  return map[score] || ''
}

function getDagScoreDescription(score) {
  if (!score) return t('views.session.dag.selectScore')
  const map = {
    1: t('views.session.dag.score1'),
    2: t('views.session.dag.score2'),
    3: t('views.session.dag.score3'),
    4: t('views.session.dag.score4'),
    5: t('views.session.dag.score5'),
  }
  return map[score] || ''
}


  function getDateValidationError(selectedDateStr) {
    if (!props.showDateInput || !goat.value?.dateOfBirth || !selectedDateStr) {
      return null
    }

    const selectedDateObj = new Date(selectedDateStr)
    const birthDate = new Date(goat.value.dateOfBirth)

    if (isNaN(birthDate.getTime())) {
      return null
    }

    if (selectedDateObj.getTime() < birthDate.getTime()) {
      const birthDateString = birthDate.toLocaleDateString()
      return t('views.selectedGoat.dateBeforeBirth', {
        date: birthDateString,
      })
    }
    if (selectedDateObj.getTime() > Date.now()) {
      return t('views.selectedGoat.dateAfterToday', {
        date: new Date().toLocaleDateString(),
      })
    }
    return null
  }

  function validateForm(data) {
    
    if (props.showDateInput && (!data.date || getDateValidationError(data.date))) {
      return false
    }
    
    // FAMACHA validation - requires both date and score
    if (props.inputMode === 'famacha') {
      return data.date && data.score !== null && data.score !== undefined
    }
    if (props.inputMode === 'dag') {
      return data.date && data.score !== null && data.score !== undefined
    }

    
    // Wound/Treatment validation - requires type, title, description, and date
    if (props.inputMode === 'wound' || props.inputMode === 'treatment') {
      return (
        data.healthType &&
        data.healthTitle?.trim() &&
        data.healthDescription?.trim() &&
        data.date
      )
    }
    
    const { type, min, max, value: validationValue } = props.validation || {}
    if (!type) return true
    
    if (type === 'range') {
      const value = (data.value ?? '').trim()
      const num = parseFloat(value.replace(',', '.'))
      return (
       !isNaN(num) &&
       num >= min &&
       num <= max &&
       !(min >= 1 && max <= 9 && !Number.isInteger(num))
      )
    }


    if (type === 'minLength') {
      const v = data.value?.trim() || ''
      return v.length >= validationValue
    }

    return true

  }

  function getValidationError(value) {
    const validation = props.validation

    if (validation.type === 'range') {
      const num = parseFloat(value.replace(',', '.'))
      if (isNaN(num)) {
        return t('views.session.invalidInput')
      }
      if (num < validation.min || num > validation.max) {
        const unit = validation.unit || ''
        return t('views.session.valueOutOfRange', {
          min: validation.min,
          max: validation.max,
          unit,
        })
      }
      if (
        validation.min >= 1 &&
        validation.max <= 9 &&
        !Number.isInteger(num)
      ) {
        return t('views.session.dag.invalid')
      }
    }

    if (validation.type === 'minLength') {
      if (value.length < validation.value) {
        return t('views.session.minLength', { min: validation.value })
      }
    }

    return null
  }

  // Get score description for FAMACHA
  function getScoreDescription(score) {
    const descriptions = {
      1: t('views.session.famacha.score1'),
      2: t('views.session.famacha.score2'),
      3: t('views.session.famacha.score3'),
      4: t('views.session.famacha.score4'),
      5: t('views.session.famacha.score5'),
    }
    return descriptions[score] || t('views.session.famacha.selectScore')
  }

  // Get short label for FAMACHA score buttons
  function getScoreShortLabel(score) {
    const labels = {
      1: t('views.session.famacha.score1'),
      2: t('views.session.famacha.score2'),
      3: t('views.session.famacha.score3'),
      4: t('views.session.famacha.score4'),
      5: t('views.session.famacha.score5'),
    }
    return labels[score] || ''
  }

  function handleSave(data) {
    // Handle FAMACHA input
    if (props.inputMode === 'famacha') {
      emit('save', {
        date: data.date,
        score: data.score,
      })
      return
    }
    if (props.inputMode === 'dag') {
      emit('save', {
      date: data.date,
      score: data.score,
      })
      return
    }


    // Handle Wound/Treatment input
    if (props.inputMode === 'wound' || props.inputMode === 'treatment') {
      emit('save', {
        type: data.healthType,
        title: data.healthTitle.trim(),
        description: data.healthDescription.trim(),
        date: data.date,
      })
      return
    }

    const value = (data.value ?? '').trim()


    if (!value) {
      alert(t('views.session.invalidInput'))
      return
    }

    // Get validation error
    const error = getValidationError(value)
    if (error) {
      alert(error)
      return
    }

    // Process value based on validation type
    let processedValue = value
    if (props.validation?.type === 'range') {
      processedValue = parseFloat(value.replace(',', '.'))
    }

    emit('save', {
      rawValue: value,
      processedValue: processedValue,
      date: props.showDateInput ? data.date : null,
    })
  }

  function handleCancel() {
    emit('cancel')
  }

  function triggerFlash() {
    isFlashing.value = true
    setTimeout(() => {
      isFlashing.value = false
    }, 300)
  }

  // Helper methods for wound/treatment modes
  function getHealthTypeOptions() {
    if (props.inputMode === 'wound') {
      return ['wound', 'disease', 'injury']
    }
    if (props.inputMode === 'treatment') {
      return ['treatment', 'vaccination']
    }
    return []
  }

  function getHealthTypeLabel(type) {
    return t(`views.health.${props.inputMode}sDetail.types.${type}`)
  }

  function getHealthRecordLabel(key) {
    return t(`views.health.${props.inputMode}sDetail.${key}`)
  }
</script>

<style scoped>
  .timer-reset-btn {
    padding: 0.75rem;
    min-width: auto;
  }

  @keyframes flash {
    0%,
    100% {
      filter: brightness(1);
    }
    50% {
      filter: brightness(1.4);
    }
  }

  .flash-animation {
    animation: flash 0.3s ease-out;
  }

  html[data-theme='dark']
    input[type='date']::-webkit-calendar-picker-indicator {
    filter: invert(1);
  }

  /* FAMACHA Score Buttons */
  .famacha-score-btn {
    width: 3rem;
    height: 3rem;
    border-radius: 0.5rem;
    font-size: 1.25rem;
    font-weight: bold;
    border: 2px solid transparent;
    transition: all 0.2s;
    color: white;
  }

  .famacha-score-5 {
    background-color: #f94943; /* rgb(249, 73, 67) - Dark red */
  }

  .famacha-score-4 {
    background-color: #ff7963; /* rgb(255, 121, 99) */
  }

  .famacha-score-3 {
    background-color: #faa89e; /* rgb(250, 168, 158) */
  }

  .famacha-score-2 {
    background-color: #fedcca; /* rgb(254, 220, 203) */
    color: #000;
  }
  .famacha-score-1 {
    background-color: #ffffff; /* White - healthy */
    color: #000;
    border: 2px solid #d1d5db;
  }

  .famacha-score-btn:hover {
    transform: scale(1.1);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }

  .famacha-score-btn--selected {
    border-color: var(--color-primary-600);
    box-shadow: 0 0 0 3px var(--color-primary-200);
    transform: scale(1.15);
  }

  /* DAG Score Buttons */
  .dag-score-btn {
    width: 3rem;
    height: 3rem;
    border-radius: 0.5rem;
    font-size: 1.25rem;
    font-weight: bold;
    border: 2px solid transparent;
    transition: all 0.2s;
    color: white;
  }
  .dag-score-5 { background-color: #f94943; /* rgb(249, 73, 67) - Dark red */ }
  .dag-score-4 { background-color: #ff7963; /* rgb(255, 121, 99) */ }
  .dag-score-3 { background-color: #faa89e; /* rgb(250, 168, 158) */ }
  .dag-score-2 { background-color: #fedcca; /* rgb(254, 220, 203) */ color: #000; }
  .dag-score-1 { background-color: #ffffff; /* White - healthy */ color: #000; border: 2px solid #d1d5db; }

  .dag-score-btn:hover {
    transform: scale(1.1);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }
  .dag-score-btn--selected {
    border-color: var(--color-primary-600);
    box-shadow: 0 0 0 3px var(--color-primary-200);
    transform: scale(1.15);
  }

  /* Dropdown select styling */
  .dropdown-select {
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 0.75rem center;
    background-size: 12px;
  }

  html[data-theme='dark'] .dropdown-select {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23ccc' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  }

/* Old dag colors
.dag-score-btn {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  font-weight: 700;
  border: 1px solid rgba(0,0,0,0.2);
  transition: transform 0.05s ease;
}
.dag-score-1 { background: #ffffff; color: #000000; }
.dag-score-2 { background: #e5e7eb; color: #000000; }
.dag-score-3 { background: #9ca3af; color: #000000; }
.dag-score-4 { background: #4b5563; color: #ffffff; }
.dag-score-5 { background: #000000; color: #ffffff; }

.dag-score-btn:active { transform: scale(0.96); }

.dag-score-btn--selected {
  outline: 2px solid var(--color-success);
  outline-offset: 2px;
}
*/
</style>